import { defineComponent as _defineComponent } from 'vue'
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ExternalIds, Scoring } from '@/@types/graphql-types';

import { numberToCompactString } from '@/helpers/NumberFormat';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getScorings, getVotesLabel, ScoringProvider, getRottenTomatoesScoreIcon } from '@/helpers/scoring-helper';

import { useRoute } from 'vue-router/composables';
import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleScoreList',
  props: {
    scores: null,
    externalIds: { default: () => ({}) },
    excluded: { default: () => [] },
    includeLinks: { type: Boolean, default: true },
    imdbOnly: { type: Boolean },
    lazy: { type: Boolean, default: true },
    showVotes: { type: Boolean },
    additionalContexts: { default: () => [] },
    jwVotes: { default: null },
    showJwVotes: { type: Boolean, default: true },
    isPosterDetail: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { scores: Scoring, externalIds: ExternalIds, excluded: ScoringProvider[], includeLinks: boolean, imdbOnly?: boolean, lazy: boolean, showVotes?: boolean, additionalContexts: SnowplowContext[], jwVotes: number | null, showJwVotes: boolean, isPosterDetail?: boolean };



const route = useRoute();

const computedJwVotes = computed(() => (props.jwVotes === null ? 0 : props.jwVotes));

const scoring = computed(() => {
	const scoring = getScorings(props.scores, props.externalIds, props.excluded, props.includeLinks);

	return scoring.map(score => {
		if (score.provider === ScoringProvider.IMDB) {
			const votes = props.scores.imdbVotes || 0;
			const votesLabel = getVotesLabel(votes);

			if (votesLabel && props.showVotes) {
				score.append = ` (${votesLabel})`;
			}
		}

		if (score.provider === ScoringProvider.TMDB && computedJwVotes.value > 30) {
			const calculatedScore = Math.round((props.scores.jwRating ?? 0) * 100);
			return {
				append: '%',
				decimals: 0,
				filterDisplay: false,
				image: '/appassets/img/jw-icon.png',
				max: 100,
				min: 0,
				name: 'JustWatch Rating',
				provider: ScoringProvider.JUSTWATCH,
				queryName: 'justwatchRating',
				score: calculatedScore <= 0 ? '0' : calculatedScore,
				step: 1,
				value: 0,
				link: '',
			};
		}

		if (score.provider === ScoringProvider.ROTTEN_TOMATOES) {
			score.image = getRottenTomatoesScoreIcon(Number(score.score), !!props.scores.certifiedFresh);
		}

		return score;
	});
});

const filteredScoring = computed(() => {
	const filtered = scoring.value.filter(score => !!score.score && showRating(score));

	if (props.imdbOnly) {
		return filtered.filter(scoring => scoring.provider === ScoringProvider.IMDB);
	}

	return filtered;
});

function showRating(score: any) {
	if (score.name === 'JustWatch Rating' && computedJwVotes.value > 30) return true;
	return score.name !== 'JustWatch Rating';
}

function formatVotes(votes: number) {
	if (!votes || votes < 30) return '';
	if (votes >= 10000) return `(${numberToCompactString(votes, 0)})`;
	return `(${numberToCompactString(votes, 1)})`;
}

function onRatingClick($event: Event, score: any) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'rating_click',
			label: score.name,
		},
		props.additionalContexts
	);

	let scoreLink = score.link;

	// Reconstruct IMDb link to use current IMDb website URL formatting
	if (route.name === 'app.detail.show.season' && score.name === 'IMDB') {
		const imdbId = props.externalIds?.imdbId;
		let idInPath = imdbId;

		if (imdbId?.includes(':')) {
			const [id] = imdbId.split(':', 2);
			idInPath = id;
		}

		scoreLink = `https://www.imdb.com/title/${idInPath}?ref_=ref_ext_justwatch`;
	}

	window.open(scoreLink);
}

return { __sfc: true,props, route, computedJwVotes, scoring, filteredScoring, showRating, formatVotes, onRatingClick }
}

})